/**
 * Import plan screen — mockup
 * .codelane/artifacts/t-27/transfer-import-diff.html L305-L682
 * (screen 1: the plan, L305-L622; screen 2: the confirm modal, L636-L682).
 * Spec: docs/superpowers/specs/2026-08-05-project-snapshot-transfer-design.md
 * §5 (diff semantics), §8.3-§8.5.
 *
 * Rendered by TransferPage once a file has been parsed into a plan. Owns the
 * whole review surface: the file card, the four summary tiles, the three
 * section cards with their master checkboxes, the fixed apply bar and the
 * type-to-confirm modal.
 *
 * The screen's one job is to make "what leaves this environment" impossible
 * to miss before anything is written. Every number on it is derived from the
 * server's plan and the human's current selection — nothing is guessed in the
 * browser, because only the server knows whether a board holds scores.
 */
import { ReactNode, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  File,
  Pencil,
  Plus,
  Trash,
  Trash2,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { AlertStack } from "../../components/Alert/AlertStack";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Card, CardBody, CardFooter, CardHeader } from "../../components/Card/Card";
import { Checkbox } from "../../components/Checkbox/Checkbox";
import { Code } from "../../components/Code/Code";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Modal } from "../../components/Modal/Modal";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import { formatMomentUTC } from "../../lib/format";
import { projectQueryKey } from "../projects/useProject";
import { applySnapshot, planSnapshot } from "./api";
import { ApplyConfirmModal, ApplyOperation } from "./ApplyConfirmModal";
import { SummaryTile, SummaryTileRow } from "./SummaryTile";
import { renderNote } from "./renderNote";
import styles from "./ImportReview.module.css";
import type {
  ApplyMode,
  ApplySelection,
  BoardChange,
  ConfigChange,
  GuildTemplatePlan,
  PhraseGroupChange,
  RosterAction,
  RosterGroupPlan,
  RosterRowPlan,
  Snapshot,
  SnapshotGuildTemplate,
  SnapshotLeaderboard,
  SnapshotPlan,
  TemplateAction,
} from "./types";

export interface ImportReviewProps {
  projectId: string;
  /** This environment's project name — the type-to-confirm word. */
  projectName: string;
  /** The parsed file, posted back verbatim on apply so the server re-diffs it. */
  snapshot: Snapshot;
  /** The server-computed plan for that file. */
  plan: SnapshotPlan;
  /** The file's own name, shown in the header card. */
  fileName: string;
  /** Back to the export screen, discarding the loaded file. */
  onCancel: () => void;
  /** Swap the loaded file for another one, straight from the header card. */
  onReplaceFile: () => void;
  /** A committed apply: the page returns to export and refreshes its history. */
  /**
   * A committed apply. `scheduledBoards` is the half of it that has not
   * happened yet, so the toast can stop short of calling a package applied.
   */
  onApplied: (appliedChanges: number, scheduledBoards: number) => void;
}

/** Which sections the human left checked. */
interface SectionSelection {
  config: boolean;
  /** The simulation's own document — a section like any other, with its own
   *  tick, its own field count and its own rows. */
  bot_settings: boolean;
  leaderboards: boolean;
  bot_phrases: boolean;
  /**
   * The bot source is two checkboxes and not one: carrying a list of guilds
   * into a project whose bot setup is entirely different is a legitimate thing
   * to want, and a checkbox has to be able to say it.
   */
  bot_roster: boolean;
  bot_guild_templates: boolean;
}

// ---------------------------------------------------------------------------
// Text helpers
// ---------------------------------------------------------------------------

/** `1 field` / `3 fields`. */
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Drops the empty parts, then joins with the mockup's middle dot. */
function joinDots(parts: string[]): string {
  return parts.filter(Boolean).join(" · ");
}

/** `12,480` — score counts are large enough to be unreadable without it. */
function count(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * `Aug 5, 2026, 12:40` from the envelope's RFC3339 stamp (mockup L314). Read
 * in the viewer's own timezone: they are checking whether this is the file
 * they exported ten minutes ago.
 */
function formatExportedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Interleaves a separator between nodes without an extra wrapper element. */
function joinNodes(nodes: ReactNode[], sep: string): ReactNode[] {
  return nodes.flatMap((n, i) => (i === 0 ? [n] : [sep, n]));
}

/** `a`, `a and b`, `a, b and c` — the punctuation the mockup's prose uses. */
function joinList(nodes: ReactNode[]): ReactNode[] {
  return nodes.flatMap((n, i) => {
    if (i === 0) return [n];
    return [i === nodes.length - 1 ? " and " : ", ", n];
  });
}

const SMALL_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
];

/**
 * A small count referred BACK to, written as a word: "for those two the file
 * wins" is a sentence, "for those 2" is a log line. The leading count that
 * introduces the quantity stays a digit — the mockup writes both ways in one
 * sentence, and it is right to.
 */
function word(n: number): string {
  return SMALL_WORDS[n] ?? String(n);
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Plan-derived shapes
// ---------------------------------------------------------------------------

/** A `locked` row applies nothing, so it never makes the section actionable. */
function configRows(plan: SnapshotPlan) {
  return plan.config.present ? plan.config.changes : [];
}
function configHasChanges(plan: SnapshotPlan): boolean {
  return configRows(plan).some((c) => c.kind !== "locked");
}

function botSettingsRows(plan: SnapshotPlan) {
  return plan.bot_settings.present ? plan.bot_settings.changes : [];
}
function botSettingsHasChanges(plan: SnapshotPlan): boolean {
  return botSettingsRows(plan).some((c) => c.kind !== "locked");
}

/**
 * The board rows the table draws.
 *
 * An `unchanged` board is reported for completeness and normally left out —
 * unless it is holding a scheduled package, because then "the file and this
 * environment already agree" has an expiry date on it: the moment that package
 * applies, the board parts company with the file again. That is a fact about
 * this import which is visible nowhere else.
 */
function boardRows(plan: SnapshotPlan): BoardChange[] {
  return plan.leaderboards.present
    ? plan.leaderboards.boards.filter(
        (b) => b.action !== "unchanged" || b.pending_count > 0,
      )
    : [];
}

/**
 * Work the import would actually do to the boards. An `unchanged` row is drawn
 * and applied by nothing, so a section holding only those must not arm Apply
 * for a run that writes nothing and then reports "0 changes".
 */
function boardsActionable(rows: BoardChange[]): boolean {
  return rows.some((b) => b.action !== "unchanged");
}

/**
 * When one row lands.
 *
 * Two rows have no choice to make and are not offered one. A board with no cut
 * to wait for can only land now; a row naming a field that only a cut can
 * write can only land then, whatever the card above it says, because the
 * package is that field's single road. Everything else takes its own override
 * if the operator set one, and the import's default otherwise.
 */
function rowMode(
  b: BoardChange,
  fallback: ApplyMode,
  overrides: Record<string, ApplyMode>,
): ApplyMode {
  if (b.action !== "update" || !b.schedulable) return "now";
  if (b.schedule_only_fields.length > 0) return "next_cycle";
  return overrides[b.ext_key] ?? fallback;
}

function phraseChanged(g: PhraseGroupChange): boolean {
  return g.added > 0 || g.deleted > 0 || g.status_changed > 0;
}

function phraseGroups(plan: SnapshotPlan): PhraseGroupChange[] {
  return plan.bot_phrases.present ? plan.bot_phrases.groups : [];
}
function phraseHasChanges(plan: SnapshotPlan): boolean {
  return phraseGroups(plan).some(phraseChanged);
}

// ── The bot source ─────────────────────────────────────────────────────────

function rosterGroups(plan: SnapshotPlan): RosterGroupPlan[] {
  return plan.bot_roster.present ? plan.bot_roster.groups : [];
}

/** One bucket's count, or zero for a bucket the plan happens not to carry. */
function rosterCount(groups: RosterGroupPlan[], action: RosterAction): number {
  return groups.find((g) => g.action === action)?.count ?? 0;
}

/**
 * Every row on both sides, counted once — the property `requireAccounted` in
 * internal/admin/snapshot/rosterdiff_test.go holds the buckets to: FIVE of them
 * are a partition of the names in play.
 *
 * `blocked` is the sixth and is skipped here, because it is an OVERLAY rather
 * than a bucket: the row it counts is already inside created, changed or kept,
 * and adding it in would count it twice and make the footer's sentence — "201 +
 * 12 + 4 + 164 + 9 accounts for every row on both sides" — add up to more rows
 * than either side has.
 *
 * The number also decides whether the section exists at all: a plan whose every
 * bucket is empty is a file with no roster in it, not a roster with nothing to
 * say.
 */
function rosterAccounted(groups: RosterGroupPlan[]): number {
  return partitionGroups(groups).reduce((n, g) => n + g.count, 0);
}

/**
 * The five buckets that partition the names in play — every bucket but
 * `blocked`.
 *
 * ONE definition, and both the sum above and the footer that prints it read
 * from it: a second copy of «which buckets are the partition» is a second place
 * for the sentence and the number under it to disagree.
 */
function partitionGroups(groups: RosterGroupPlan[]): RosterGroupPlan[] {
  return groups.filter((g) => g.action !== "blocked");
}

/**
 * Does this action WRITE anything?
 *
 * `kept` does not — nothing happens to a kept row — and neither does
 * `conflict`, which is the plan refusing. `blocked` is not here either, and for
 * a different reason: it is not an action at all. A blocked row IS written, but
 * it is written as one of created, changed or kept, and those already say so —
 * counting the overlay as well would arm Apply for a section whose only
 * "changes" are rows nothing happens to.
 *
 * One predicate over both sections, because a `TemplateAction` is a
 * `RosterAction` and the question is the same question.
 */
function writesSomething(action: RosterAction): boolean {
  return (
    action === "created" || action === "changed" || action === "removed"
  );
}

/** Work the import would actually do to the roster. */
function rosterActionable(groups: RosterGroupPlan[]): boolean {
  return groups.some((g) => writesSomething(g.action) && g.count > 0);
}

function templateRows(plan: SnapshotPlan): GuildTemplatePlan[] {
  return plan.bot_guild_templates.present
    ? plan.bot_guild_templates.templates
    : [];
}

function templateCount(
  rows: GuildTemplatePlan[],
  action: TemplateAction,
): number {
  return rows.filter((t) => t.action === action).length;
}

/**
 * Templates that will arrive blocked, counted off the FLAG.
 *
 * Not off the action, which is the whole of the difference from
 * `templateCount`: a blocked template is also one of created, changed and kept,
 * so counting it as an action would count it twice — exactly what
 * `TemplateTotals` in internal/admin/snapshot/templatediff.go says it must not
 * do.
 */
function templateBlocked(rows: GuildTemplatePlan[]): number {
  return rows.filter((t) => t.blocked).length;
}

/** rosterActionable, over rows instead of buckets. */
function templatesActionable(rows: GuildTemplatePlan[]): boolean {
  return rows.some((t) => writesSomething(t.action));
}

function defaultSections(plan: SnapshotPlan): SectionSelection {
  return {
    config: configHasChanges(plan),
    bot_settings: botSettingsHasChanges(plan),
    leaderboards: boardsActionable(boardRows(plan)),
    bot_phrases: phraseHasChanges(plan),
    bot_roster: rosterAccounted(rosterGroups(plan)) > 0,
    bot_guild_templates: templateRows(plan).length > 0,
  };
}

/** Every listed board starts checked; nothing on the table is unappliable. */
function defaultKeys(plan: SnapshotPlan): string[] {
  return boardRows(plan).map((b) => b.ext_key);
}

// ---------------------------------------------------------------------------

export function ImportReview({
  projectId,
  projectName,
  snapshot,
  plan: planProp,
  fileName,
  onCancel,
  onReplaceFile,
  onApplied,
}: ImportReviewProps) {
  const { push } = useToast();
  const qc = useQueryClient();

  const [plan, setPlan] = useState(planProp);
  const [sections, setSections] = useState<SectionSelection>(() =>
    defaultSections(planProp),
  );
  const [keys, setKeys] = useState<string[]>(() => defaultKeys(planProp));
  /**
   * The moment the whole import lands on, and the per-board overrides of it.
   *
   * `next_cycle` by default: an import is the one write to a live board nobody
   * is watching the clock for, and the cost of the two choices is not
   * symmetric — a change that waited a week for its cut can still be applied
   * by hand, while a `sort` that reordered a running week cannot be taken back.
   */
  const [applyMode, setApplyMode] = useState<ApplyMode>("next_cycle");
  const [boardModes, setBoardModes] = useState<Record<string, ApplyMode>>({});
  /** Set after a 409 forced the plan to be recomputed under the human's feet. */
  const [reloaded, setReloaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** Which document's raw JSON the modal is showing, if any. Two sections have
   *  one now — the project's config and the simulation's own document — and a
   *  single boolean would have opened whichever was written last. */
  const [jsonOpen, setJsonOpen] = useState<"config" | "bot_settings" | null>(
    null,
  );
  const [applying, setApplying] = useState(false);
  /**
   * Which roster buckets show their sample rows. Two hundred rows are not a
   * list anybody reads, so the plan carries counts with a couple of samples
   * each and the screen opens the two whose rows say something their count does
   * not: what is arriving, and the rows an operator has to go and rename.
   *
   * `conflict` is drawn folded, as the mockup draws it: its heading already
   * names the offending key, so the sample rows underneath repeat that sentence
   * once per row.
   */
  const [openBuckets, setOpenBuckets] = useState<RosterAction[]>([
    "created",
    "blocked",
  ]);

  // A new file (or a re-plan by the page) resets the whole selection. Adjusting
  // state during render is the documented way to derive from props without an
  // extra commit; see React's "you might not need an effect".
  const [seenProp, setSeenProp] = useState(planProp);
  if (seenProp !== planProp) {
    setSeenProp(planProp);
    setPlan(planProp);
    setSections(defaultSections(planProp));
    setKeys(defaultKeys(planProp));
    // The overrides are keyed by ext_key, and a new file is a new set of them.
    setBoardModes({});
    setReloaded(false);
  }

  const cfgRows = configRows(plan);
  const boards = boardRows(plan);
  const groups = phraseGroups(plan);
  const rGroups = rosterGroups(plan);
  const tplRows = templateRows(plan);
  const hasConfig = configHasChanges(plan);
  const botRows = botSettingsRows(plan);
  const hasBotSettings = botSettingsHasChanges(plan);
  const hasBoards = boards.length > 0;
  const boardsOn = boardsActionable(boards);
  const hasPhrases = phraseHasChanges(plan);
  // Same rule for the two bot-source sections, with `kept` playing the part
  // `conflict` plays above: the card is drawn so an operator can see that 164
  // rows are beyond the file's reach, and it arms nothing.
  const hasRoster = rosterAccounted(rGroups) > 0;
  const rosterOn = rosterActionable(rGroups);
  const hasTemplates = tplRows.length > 0;
  const templatesOn = templatesActionable(tplRows);
  const anyChange =
    hasConfig || hasBotSettings || boardsOn || hasPhrases || rosterOn || templatesOn;

  /** The file's own board definitions, for the fields the plan does not carry. */
  const fileBoards = useMemo(
    () => new Map((snapshot.leaderboards ?? []).map((b) => [b.ext_key, b])),
    [snapshot],
  );

  const selectedKeys = useMemo(
    () => boards.filter((b) => keys.includes(b.ext_key)).map((b) => b.ext_key),
    [boards, keys],
  );

  function toggleKey(extKey: string, next: boolean) {
    setKeys((prev) =>
      next ? [...prev, extKey] : prev.filter((k) => k !== extKey),
    );
  }

  function pickBoardMode(extKey: string, next: ApplyMode) {
    setBoardModes((prev) => ({ ...prev, [extKey]: next }));
  }

  // ── What the current selection would do ──────────────────────────────────

  const picked = useMemo(
    () => boards.filter((b) => selectedKeys.includes(b.ext_key)),
    [boards, selectedKeys],
  );

  const s = plan.summary;
  const lbOn = sections.leaderboards;
  const rosterPicked = sections.bot_roster;
  const tplPicked = sections.bot_guild_templates;
  /**
   * What the import will refuse to write. Skipped by the importer and not by
   * the human — but only a section that is actually applied can skip anything.
   *
   * No board is ever in here. A board the file disagrees with is an ordinary
   * update that waits for a cut, so what is left are the roster rows and the
   * guild templates naming a card key this project never declared.
   */
  const conflictsSkipped =
    (rosterPicked ? rosterCount(rGroups, "conflict") : 0) +
    (tplPicked ? templateCount(tplRows, "conflict") : 0);

  const sectionsOn =
    (hasConfig && sections.config ? 1 : 0) +
    (hasBotSettings && sections.bot_settings ? 1 : 0) +
    (boardsOn && sections.leaderboards ? 1 : 0) +
    (hasPhrases && sections.bot_phrases ? 1 : 0) +
    (rosterOn && sections.bot_roster ? 1 : 0) +
    (templatesOn && sections.bot_guild_templates ? 1 : 0);

  /** The board rows that will wait for their own cut, under the current choice. */
  const scheduledBoards = useMemo(
    () =>
      lbOn
        ? picked.filter((b) => rowMode(b, applyMode, boardModes) === "next_cycle")
        : [],
    [lbOn, picked, applyMode, boardModes],
  );

  /**
   * Only the checked boards the operator actually overruled travel in the
   * request. A row the server would park anyway — one naming a field no
   * immediate write can carry — is left out: repeating a rule the server owns
   * is how the two copies of it start to disagree.
   */
  const boardApplyMode = useMemo(() => {
    const out: Record<string, ApplyMode> = {};
    for (const b of picked) {
      if (b.action !== "update" || !b.schedulable) continue;
      if (b.schedule_only_fields.length > 0) continue;
      const chosen = boardModes[b.ext_key];
      if (chosen && chosen !== applyMode) out[b.ext_key] = chosen;
    }
    return out;
  }, [picked, boardModes, applyMode]);

  const selection: ApplySelection = {
    config: sections.config,
    bot_settings: sections.bot_settings,
    leaderboards: sections.leaderboards,
    leaderboard_keys: sections.leaderboards ? selectedKeys : [],
    apply_mode: applyMode,
    ...(Object.keys(boardApplyMode).length > 0
      ? { board_apply_mode: boardApplyMode }
      : {}),
    bot_phrases: sections.bot_phrases,
    bot_roster: sections.bot_roster,
    bot_guild_templates: sections.bot_guild_templates,
  };

  // ── Apply ────────────────────────────────────────────────────────────────

  async function apply() {
    if (applying) return;
    setApplying(true);
    try {
      const result = await applySnapshot(projectId, {
        snapshot,
        selection,
        etag: plan.etag,
      });
      // The success toast belongs to onApplied, which also owns the return to
      // the export screen — pushing one here too would stack two identical
      // toasts on every apply.
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ["project", projectId, "activity"] });
      setConfirmOpen(false);
      onApplied(result.applied, result.boards_scheduled ?? 0);
      return;
    } catch (err) {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        // Never retry silently: the project moved, so the plan the human read
        // is not the plan that would have been applied.
        setConfirmOpen(false);
        try {
          const fresh = await planSnapshot(projectId, snapshot);
          setPlan(fresh);
          setSections(defaultSections(fresh));
          setKeys(defaultKeys(fresh));
          setReloaded(true);
        } catch {
          push({ variant: "danger", message: describeError("internal") });
        }
        return;
      }
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    } finally {
      setApplying(false);
    }
  }

  // ── The file card ────────────────────────────────────────────────────────

  // The mockup shows the bare schema name; the envelope carries it vendored.
  const schemaLabel = schemaName(plan.schema);

  const fileCard = (
    <Card>
      <CardBody>
        <div className={styles.fileCard}>
          <span className={styles.fileIcon} aria-hidden="true">
            <File size={18} />
          </span>
          <div>
            <div className={styles.fileName}>{fileName}</div>
            <div className={styles.fileMeta}>
              Taken from <b>{plan.source.host}</b> · project “
              {plan.source.project_name}” · {formatExportedAt(plan.exported_at)}{" "}
              · schema <code>{schemaLabel}</code>
            </div>
          </div>
          <div className={styles.fileRight}>
            <Badge variant="success">Valid file</Badge>
            <Button variant="secondary" size="sm" onClick={onReplaceFile}>
              Replace file
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  if (!anyChange) {
    return (
      <div className={styles.root}>
        {fileCard}
        <EmptyState
          icon={<CheckCircle2 size={28} aria-hidden="true" />}
          title="Settings already match the snapshot"
          description="Nothing in this file differs from what this environment already has, so there is nothing to apply."
          action={
            <Button variant="secondary" onClick={onReplaceFile}>
              Replace file
            </Button>
          }
        />
      </div>
    );
  }

  // ── Summary tiles (mockup L325-L358) ─────────────────────────────────────

  const tiles = (
    <SummaryTileRow>
      <SummaryTile
        label="Changed"
        value={s.changed}
        icon={<Pencil size={14} />}
        sub={
          joinDots([
            s.config_fields ? plural(s.config_fields, "config field", "config fields") : "",
            s.bot_settings_fields
              ? plural(s.bot_settings_fields, "bot setting", "bot settings")
              : "",
            s.boards_changed ? plural(s.boards_changed, "board", "boards") : "",
            s.phrase_groups_changed
              ? plural(s.phrase_groups_changed, "phrase group", "phrase groups")
              : "",
            s.roster_rows_changed
              ? plural(s.roster_rows_changed, "roster row", "roster rows")
              : "",
            s.guild_templates_changed
              ? plural(s.guild_templates_changed, "guild template", "guild templates")
              : "",
          ]) || "none"
        }
      />
      <SummaryTile
        label="Created"
        value={s.created}
        tone="ok"
        icon={<Plus size={14} />}
        sub={
          joinDots([
            s.boards_created ? plural(s.boards_created, "leaderboard", "leaderboards") : "",
            s.phrases_added ? plural(s.phrases_added, "phrase", "phrases") : "",
            s.roster_rows_created
              ? plural(s.roster_rows_created, "roster row", "roster rows")
              : "",
            s.guild_templates_created
              ? plural(s.guild_templates_created, "guild template", "guild templates")
              : "",
          ]) || "none"
        }
      />
      <SummaryTile
        label="Removed"
        value={s.removed}
        tone="warn"
        icon={<Trash2 size={14} />}
        sub={
          joinDots([
            s.boards_archived ? `${plural(s.boards_archived, "board", "boards")} archived` : "",
            s.phrases_deleted ? `${plural(s.phrases_deleted, "phrase", "phrases")} deleted` : "",
            s.roster_rows_removed
              ? `${plural(s.roster_rows_removed, "roster row", "roster rows")} removed`
              : "",
            s.guild_templates_removed
              ? `${plural(s.guild_templates_removed, "guild template", "guild templates")} removed`
              : "",
          ]) || "none"
        }
      />
      <SummaryTile
        label="Conflicts"
        value={s.conflicts}
        tone="danger"
        icon={<AlertTriangle size={14} />}
        sub={
          s.conflicts > 0
            ? "skipped — resolve it by hand"
            : "nothing is skipped"
        }
      />
    </SummaryTileRow>
  );

  // ── The mirroring alert (mockup L360-L366) ───────────────────────────────

  const archivedBoards = boards.filter((b) => b.action === "archive" && !b.hard_delete);
  const deletedBoards = boards.filter((b) => b.action === "archive" && b.hard_delete);

  const mirrorParts: ReactNode[] = [];
  if (archivedBoards.length > 0) {
    mirrorParts.push(
      <span key="archived">
        {archivedBoards.length === 1 ? "leaderboard " : "leaderboards "}
        {joinNodes(
          archivedBoards.map((b) => <code key={b.ext_key}>{b.ext_key}</code>),
          ", ",
        )}
        {archivedBoards.length === 1 ? " gets archived" : " get archived"}
      </span>,
    );
  }
  if (deletedBoards.length > 0) {
    mirrorParts.push(
      <span key="deleted">
        {deletedBoards.length === 1 ? "leaderboard " : "leaderboards "}
        {joinNodes(
          deletedBoards.map((b) => <code key={b.ext_key}>{b.ext_key}</code>),
          ", ",
        )}
        {deletedBoards.length === 1 ? " gets deleted" : " get deleted"}
      </span>,
    );
  }
  if (s.phrases_deleted > 0) {
    mirrorParts.push(
      <span key="phrases">
        {plural(s.phrases_deleted, "phrase is", "phrases are")} deleted
      </span>,
    );
  }
  // "unspent" is the word that makes these two true: mirroring never reaches a
  // row a bot or a guild is standing on, so what leaves is only what nothing
  // was built from.
  if (s.roster_rows_removed > 0) {
    mirrorParts.push(
      <span key="roster">
        {plural(s.roster_rows_removed, "unspent roster row is", "unspent roster rows are")}{" "}
        deleted
      </span>,
    );
  }
  if (s.guild_templates_removed > 0) {
    mirrorParts.push(
      <span key="templates">
        {plural(
          s.guild_templates_removed,
          "unspent guild template is",
          "unspent guild templates are",
        )}{" "}
        deleted
      </span>,
    );
  }

  const mirrorAlert = mirrorParts.length > 0 && (
    <Alert
      variant="danger"
      title="Selected sections are mirrored to the file"
      icon={<AlertTriangle size={14} />}
    >
      Whatever the file does not contain leaves this environment too:{" "}
      {joinList(mirrorParts)}. Uncheck a section to leave it untouched.
    </Alert>
  );

  // ── Section 1: config (mockup L369-L444) ─────────────────────────────────

  const configFieldCount = cfgRows.filter((c) => c.kind !== "locked").length;

  const configCard = hasConfig && (
    <Card role="group" aria-label="Project configuration section">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <Checkbox
            checked={sections.config}
            onChange={(next) => setSections((p) => ({ ...p, config: next }))}
            aria-label="Project configuration"
          />
          <h3 className={styles.secTitle}>Project configuration</h3>
          <span className={styles.apiName}>games.config</span>
        </div>
        <span className={styles.secSummary}>
          <Badge variant="info">{plural(configFieldCount, "field", "fields")}</Badge>
        </span>
      </CardHeader>
      <CardBody>
        <DiffRows rows={cfgRows} />
      </CardBody>
      <CardFooter className={styles.secFoot}>
        <span>
          {plan.config.unchanged_fields > 0
            ? `The other ${plan.config.unchanged_fields} ${
                plan.config.unchanged_fields === 1
                  ? "field already matches and is"
                  : "fields already match and are"
              } not listed.`
            : "Every field of the blob is listed above."}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setJsonOpen("config")}
        >
          Show full JSON diff
        </Button>
      </CardFooter>
    </Card>
  );

  // ── Section 2: bot settings ──────────────────────────────────────────────
  //
  // An ordinary section, drawn exactly like the one above it, and that is the
  // point: the simulation's balance used to arrive as a run of `bots.…` rows
  // inside the config card, indistinguishable from a chat limit. Its paths here
  // are read from its own document's root — `population.target_members` — so
  // there is no prefix naming a place in games.config that no longer exists.

  const botSettingsCard = hasBotSettings && (
    <Card role="group" aria-label="Bot settings section">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <Checkbox
            checked={sections.bot_settings}
            onChange={(next) =>
              setSections((p) => ({ ...p, bot_settings: next }))
            }
            aria-label="Bot settings"
          />
          <h3 className={styles.secTitle}>Bot settings</h3>
          <span className={styles.apiName}>bot_settings</span>
        </div>
        <span className={styles.secSummary}>
          <Badge variant="info">
            {plural(
              botRows.filter((c) => c.kind !== "locked").length,
              "field",
              "fields",
            )}
          </Badge>
        </span>
      </CardHeader>
      <CardBody>
        <DiffRows rows={botRows} />
      </CardBody>
      <CardFooter className={styles.secFoot}>
        <span>
          {plan.bot_settings.unchanged_fields > 0
            ? `The other ${plan.bot_settings.unchanged_fields} ${
                plan.bot_settings.unchanged_fields === 1
                  ? "field already matches and is"
                  : "fields already match and are"
              } not listed.`
            : "Every field of the document is listed above."}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setJsonOpen("bot_settings")}
        >
          Show full JSON diff
        </Button>
      </CardFooter>
    </Card>
  );

  // ── Section 3: leaderboards (mockup L447-L560) ───────────────────────────

  const byAction = {
    update: boards.filter((b) => b.action === "update").length,
    create: boards.filter((b) => b.action === "create").length,
  };

  // ── When does it apply (mockup L330-L357) ────────────────────────────────
  //
  // Offered only when something could actually wait. With no schedulable board
  // in the plan the two options describe the same outcome, and a control whose
  // halves do the same thing teaches an operator the wrong thing about the
  // next import, where they will not.

  const whenCard = s.boards_schedulable > 0 && (
    <Card role="group" aria-label="When does it apply">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <h3 className={styles.secTitle}>When does it apply</h3>
          <span className={styles.apiName}>apply_mode</span>
        </div>
        <span className={styles.secSummary}>
          <Badge variant="info">
            {plural(s.boards_schedulable, "board update", "board updates")} can
            wait
          </Badge>
        </span>
      </CardHeader>
      <CardBody>
        <div className={styles.applyModes} role="radiogroup" aria-label="Apply mode">
          <ApplyModeCard
            value="now"
            current={applyMode}
            title="Apply now"
            onSelect={setApplyMode}
          >
            Everything in the plan lands the moment you confirm — including on
            boards whose cycle is running. A new <code>sort</code> reorders the
            week players are already playing.
          </ApplyModeCard>
          <ApplyModeCard
            value="next_cycle"
            current={applyMode}
            title="Schedule board updates for the next cycle"
            onSelect={setApplyMode}
          >
            Each updated board keeps its current configuration until its own
            next cut, then takes the file&apos;s values as one package.
            Everything the plan cannot postpone still applies now.
          </ApplyModeCard>
        </div>
        <div className={styles.modeNote}>
          The choice is <b>atomic per board, not per import</b>: a weekly board
          and a daily one cut at different moments, so there is no single
          instant this file could land on. This card sets the default for every
          updated board; the <b>When</b> column below is where you overrule it
          for one of them.
        </div>
      </CardBody>
    </Card>
  );

  // ── The two rows that used to be impossible (mockup L374-L380) ───────────
  //
  // Which of the two a row WOULD have been is still readable off the plan,
  // because the rule that decided it — scores or no scores — is still on the
  // wire. Nothing on this screen is guessed: a row that never was a recreate
  // must not be described as one.

  const formerlyImpossible = boards.filter(
    (b) => b.action === "update" && b.schedule_only_fields.length > 0,
  );
  const wouldRecreate = formerlyImpossible.filter((b) => b.scores === 0).length;
  const wouldConflict = formerlyImpossible.length - wouldRecreate;
  const strandedFields = [
    ...new Set(formerlyImpossible.flatMap((b) => b.schedule_only_fields)),
  ];

  const formerlyAlert = formerlyImpossible.length > 0 && (
    <Alert
      variant="info"
      icon={<Clock size={14} />}
      title={`${capitalise(word(formerlyImpossible.length))} ${
        formerlyImpossible.length === 1 ? "row" : "rows"
      } here used to be ${[
        wouldRecreate > 0
          ? wouldRecreate === 1
            ? "a recreate"
            : `${word(wouldRecreate)} recreates`
          : "",
        wouldConflict > 0
          ? wouldConflict === 1
            ? "a conflict"
            : `${word(wouldConflict)} conflicts`
          : "",
      ]
        .filter(Boolean)
        .join(" and ")}`}
    >
      {joinList(strandedFields.map((f) => <code key={f}>{f}</code>))} could not
      be patched at all, so the import had to drop a board and build it again —
      or refuse and hand it back to a human.{" "}
      {strandedFields.length === 1
        ? "It is an ordinary update now; it simply waits"
        : strandedFields.length === 2
          ? "Both are ordinary updates now; they simply wait"
          : "They are all ordinary updates now; they simply wait"}{" "}
      for a cut. <code>ext_key</code> is the only field left that can still
      force either, and it is the key rows are matched by, so it never differs.
    </Alert>
  );

  const boardsCard = hasBoards && (
    <Card role="group" aria-label="Leaderboards & rewards section">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <Checkbox
            checked={sections.leaderboards}
            onChange={(next) =>
              setSections((p) => ({ ...p, leaderboards: next }))
            }
            aria-label="Leaderboards & rewards"
          />
          <h3 className={styles.secTitle}>Leaderboards &amp; rewards</h3>
          <span className={styles.apiName}>
            leaderboards + leaderboard_rewards
          </span>
        </div>
        <span className={styles.secSummary}>
          {byAction.update > 0 && (
            <Badge variant="info">{byAction.update} updated</Badge>
          )}
          {byAction.create > 0 && (
            <Badge variant="success">{byAction.create} created</Badge>
          )}
          {archivedBoards.length > 0 && (
            <Badge variant="warning">{archivedBoards.length} archived</Badge>
          )}
          {deletedBoards.length > 0 && (
            <Badge variant="warning">{deletedBoards.length} deleted</Badge>
          )}
        </span>
      </CardHeader>
      <CardBody className={styles.tableBody}>
        {formerlyAlert && (
          <div className={styles.tableNote}>{formerlyAlert}</div>
        )}
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 44 }} />
              <th>ext_key</th>
              <th style={{ width: 120 }}>Action</th>
              <th>What happens</th>
              <th style={{ width: 230 }}>When</th>
              <th style={{ width: 100 }}>Rewards</th>
            </tr>
          </thead>
          <tbody>
            {boards.map((b) => (
              <tr key={b.ext_key} className={rowClass(b)}>
                <td>
                  <Checkbox
                    checked={keys.includes(b.ext_key)}
                    onChange={(next) => toggleKey(b.ext_key, next)}
                    aria-label={b.ext_key}
                  />
                </td>
                <td>
                  <div className={styles.key}>{b.ext_key}</div>
                  <div className={styles.sub}>{b.name}</div>
                </td>
                <td>{actionBadge(b)}</td>
                <td>{whatHappens(b, fileBoards.get(b.ext_key))}</td>
                <td>
                  <WhenCell
                    board={b}
                    mode={rowMode(b, applyMode, boardModes)}
                    onPick={(next) => pickBoardMode(b.ext_key, next)}
                  />
                </td>
                <td>{rewardsCell(b)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
      <CardFooter className={styles.secFoot}>
        <span>
          Matched by <code>ext_key</code>. Cycles, scores and granted rewards
          are neither in the snapshot nor touched by it.
          {s.boards_with_pending_changes > 0 && (
            <>
              {" "}
              {plural(s.boards_with_pending_changes, "board has", "boards have")}{" "}
              scheduled changes; the file carries the configuration as it is
              today.
            </>
          )}
        </span>
      </CardFooter>
    </Card>
  );

  // ── Section 3: phrases (mockup L563-L602) ────────────────────────────────

  // A category with any changed locale is listed in full — seeing "+12 in ru"
  // without "en is untouched" next to it invites the wrong conclusion. Only
  // categories where nothing at all moves fold into the trailing row.
  const touchedCategories = new Set(
    groups.filter(phraseChanged).map((g) => g.category),
  );
  const listedGroups = groups.filter((g) => touchedCategories.has(g.category));
  const foldedGroups = groups.filter((g) => !touchedCategories.has(g.category));

  const folded = {
    categories: new Set(foldedGroups.map((g) => g.category)).size,
    locales: [...new Set(foldedGroups.map((g) => g.locale))],
    inFile: foldedGroups.reduce((a, g) => a + g.in_file, 0),
    here: foldedGroups.reduce((a, g) => a + g.here, 0),
  };

  const phraseTotals = groups.reduce(
    (a, g) => ({
      added: a.added + g.added,
      deleted: a.deleted + g.deleted,
      // Matched on both sides and left alone — equally `here - deleted -
      // status_changed`; the two agree by construction of the diff.
      unchanged: a.unchanged + (g.in_file - g.added - g.status_changed),
      statusChanged: a.statusChanged + g.status_changed,
    }),
    { added: 0, deleted: 0, unchanged: 0, statusChanged: 0 },
  );

  const phrasesCard = hasPhrases && (
    <Card role="group" aria-label="Bot phrases section">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <Checkbox
            checked={sections.bot_phrases}
            onChange={(next) =>
              setSections((p) => ({ ...p, bot_phrases: next }))
            }
            aria-label="Bot phrases"
          />
          <h3 className={styles.secTitle}>Bot phrases</h3>
          <span className={styles.apiName}>bot_phrases</span>
        </div>
        <span className={styles.secSummary}>
          {phraseTotals.added > 0 && (
            <Badge variant="success">+{phraseTotals.added}</Badge>
          )}
          {phraseTotals.deleted > 0 && (
            <Badge variant="danger">−{phraseTotals.deleted}</Badge>
          )}
          {phraseTotals.statusChanged > 0 && (
            <Badge variant="warning">~{phraseTotals.statusChanged}</Badge>
          )}
          <Badge variant="neutral">{phraseTotals.unchanged} unchanged</Badge>
        </span>
      </CardHeader>
      <CardBody className={styles.tableBody}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Category</th>
              <th style={{ width: 90 }}>Locale</th>
              <th style={{ width: 110 }}>In file</th>
              <th style={{ width: 110 }}>Here</th>
              <th style={{ width: 130 }}>Change</th>
            </tr>
          </thead>
          <tbody>
            {listedGroups.map((g) => (
              <tr
                key={`${g.category}/${g.locale}`}
                className={g.deleted > 0 ? styles.removed : undefined}
              >
                <td className={styles.key}>{g.category}</td>
                <td className={styles.key}>{g.locale}</td>
                <td className={styles.num}>{g.in_file}</td>
                <td className={styles.num}>{g.here}</td>
                <td>{phraseChangeCell(g)}</td>
              </tr>
            ))}
            {foldedGroups.length > 0 && (
              <tr>
                <td className={styles.key}>
                  + {plural(folded.categories, "category", "categories")}
                </td>
                <td className={styles.key}>{folded.locales.join(", ")}</td>
                <td className={styles.num}>{folded.inFile}</td>
                <td className={styles.num}>{folded.here}</td>
                <td>
                  <span className={styles.deltaNone}>unchanged</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CardBody>
      <CardFooter className={styles.secFoot}>
        <span>
          Matched by the <code>(category, locale, text)</code> triple — the same
          key the existing phrase import uses.
        </span>
      </CardFooter>
    </Card>
  );

  // ── Section 4: the bot roster (mockup L427-L463) ─────────────────────────

  const rosterConflicts = rosterCount(rGroups, "conflict");
  const templateConflicts = templateCount(tplRows, "conflict");
  const tplBlocked = templateBlocked(tplRows);

  const rosterCard = hasRoster && (
    <Card role="group" aria-label="Bot roster section">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <Checkbox
            checked={sections.bot_roster}
            onChange={(next) => setSections((p) => ({ ...p, bot_roster: next }))}
            aria-label="Bot roster"
          />
          <h3 className={styles.secTitle}>Bot roster</h3>
          <span className={styles.apiName}>bot_roster</span>
        </div>
        <span className={styles.secSummary}>
          <span className={styles.apiName}>
            {plural(plan.bot_roster.rows, "row", "rows")} in the file
          </span>
        </span>
      </CardHeader>
      <CardBody className={styles.groupBody}>
        {rGroups
          .filter((g) => g.count > 0)
          .map((g) => (
            <RosterBucket
              key={g.action}
              group={g}
              open={openBuckets.includes(g.action)}
              onToggle={() =>
                setOpenBuckets((prev) =>
                  prev.includes(g.action)
                    ? prev.filter((a) => a !== g.action)
                    : [...prev, g.action],
                )
              }
            />
          ))}
      </CardBody>
      <CardFooter className={styles.secFoot}>
        {/* The five that PARTITION the names in play. «Will arrive blocked» is
            not a term of this sum — the rows it counts are already inside the
            five, and adding it would print a total larger than either side. */}
        <span>
          {partitionGroups(rGroups)
            .filter((g) => g.count > 0)
            .map((g) => g.count)
            .join(" + ")}{" "}
          accounts for every row on both sides
        </span>
        <span>
          Unspent rows are mirrored to the file; spent ones are never touched
        </span>
      </CardFooter>
    </Card>
  );

  // ── Section 5: guild templates (mockup L465-L479) ────────────────────────

  const templatesCard = hasTemplates && (
    <Card role="group" aria-label="Guild templates section">
      <CardHeader>
        <div className={styles.secHeadLeft}>
          <Checkbox
            checked={sections.bot_guild_templates}
            onChange={(next) =>
              setSections((p) => ({ ...p, bot_guild_templates: next }))
            }
            aria-label="Guild templates"
          />
          <h3 className={styles.secTitle}>Guild templates</h3>
          <span className={styles.apiName}>bot_guild_templates</span>
        </div>
        <span className={styles.secSummary}>
          {/* The overlay, said once for the whole table. The roster says it
              with a bucket of its own; a table of named rows says it here,
              because every row that carries it is already visible below. */}
          {tplBlocked > 0 && (
            <Badge variant="danger">
              {tplBlocked} will arrive blocked
            </Badge>
          )}
          <span className={styles.apiName}>
            {plural(plan.bot_guild_templates.rows, "row", "rows")} in the file
          </span>
        </span>
      </CardHeader>
      <CardBody className={styles.tableBody}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Template</th>
              <th>Here</th>
              <th>In the file</th>
              <th style={{ width: 240 }}>Result</th>
            </tr>
          </thead>
          <tbody>
            {tplRows.map((t) => (
              <tr key={t.name} className={templateRowClass(t)}>
                <td className={styles.templateName}>{t.name}</td>
                <td>{templateSide(t.here)}</td>
                <td>{templateSide(t.in_file)}</td>
                <td>
                  {templateBadge(t)}
                  {(t.reason || templateInvisibleChange(t)) && (
                    <div className={styles.sub}>
                      {t.reason ?? templateInvisibleChange(t)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
      <CardFooter className={styles.secFoot}>
        <span>
          Creating the guilds themselves is still a button on the Bots page
        </span>
        <span>An import never stands up a guild</span>
      </CardFooter>
    </Card>
  );

  // The one alert the two sections share: a key nobody declared here (mockup
  // L481-L482). It is a conflict rather than a silent drop for the config
  // diff's reason — a card the platform would refuse from the game is a card
  // no bot may carry.
  const cardKeyAlert = (rosterConflicts > 0 || templateConflicts > 0) && (
    <Alert
      variant="warn"
      title="A key this project does not declare is a conflict, not a silent drop"
      icon={<AlertTriangle size={14} />}
    >
      {[
        rosterConflicts > 0
          ? plural(rosterConflicts, "roster row", "roster rows")
          : "",
        templateConflicts > 0
          ? plural(templateConflicts, "guild template", "guild templates")
          : "",
      ]
        .filter(Boolean)
        .join(" and ")}{" "}
      {rosterConflicts + templateConflicts === 1 ? "carries" : "carry"} a{" "}
      {/* The two prefixes cardConflictReason actually emits, and no third one:
          a roster row's card is stat:/meta:, a template's metadata is meta:,
          and «metadata key» named a thing the reasons above never say — so a
          template-only conflict read as carrying a stat: key, which a template
          cannot do. */}
      <code>stat:</code> or <code>meta:</code> key this project&apos;s own
      schema has no declaration for. They are listed above and applied by
      nothing — the same rule the config diff already uses, and the same reason:
      a card the platform would refuse from the game is a card no bot may carry.
      Declare the key first, then import again.
    </Alert>
  );

  // ── The confirm modal's operation list (mockup L651-L672) ────────────────

  const operations: ApplyOperation[] = [];
  if (sections.config && s.config_fields > 0) {
    operations.push({
      id: "config",
      tone: "ok",
      icon: <Check size={13} />,
      label: (
        <>
          <b>games.config</b> — {plural(s.config_fields, "field", "fields")}{" "}
          overwritten
        </>
      ),
    });
  }

  // Its own line and not a clause on the config's: the two documents are in
  // two tables now, and an operator confirming an import has to be able to see
  // that the simulation is being overwritten even when the config is not.
  if (sections.bot_settings && s.bot_settings_fields > 0) {
    operations.push({
      id: "bot_settings",
      tone: "ok",
      icon: <Check size={13} />,
      label: (
        <>
          <b>bot_settings</b> —{" "}
          {plural(s.bot_settings_fields, "field", "fields")} overwritten
        </>
      ),
    });
  }

  const createdBoards = lbOn ? picked.filter((b) => b.action === "create") : [];
  const addedPhrases = sections.bot_phrases ? s.phrases_added : 0;
  if (createdBoards.length > 0 || addedPhrases > 0) {
    const boardPart =
      createdBoards.length > 0 ? (
        <>
          {createdBoards.length === 1 ? "Leaderboard " : "Leaderboards "}
          {joinNodes(
            createdBoards.map((b) => <b key={b.ext_key}>{b.ext_key}</b>),
            ", ",
          )}
        </>
      ) : null;
    const phrasePart =
      addedPhrases > 0 ? plural(addedPhrases, "phrase", "phrases") : null;
    operations.push({
      id: "create",
      tone: "ok",
      icon: <Plus size={13} />,
      label: (
        <>
          {boardPart}
          {boardPart && phrasePart ? " and " : null}
          {phrasePart}
          {" will be created"}
        </>
      ),
    });
  }

  if (lbOn) {
    for (const b of picked.filter((x) => x.action === "archive")) {
      operations.push({
        id: `archive-${b.ext_key}`,
        tone: b.hard_delete ? "danger" : "warn",
        icon: b.hard_delete ? <Trash size={13} /> : <Archive size={13} />,
        label: (
          <>
            Leaderboard <b>{b.ext_key}</b>{" "}
            {b.hard_delete ? (
              <>
                will be deleted <i>(it holds no scores)</i>
              </>
            ) : (
              <>
                will be archived <i>(its {count(b.scores)} scores are kept)</i>
              </>
            )}
            {/* Archiving a board takes its package with it, and an operator
                who scheduled one has to learn that here rather than from its
                absence tomorrow. */}
            {b.pending_count > 0 && (
              <>
                , and the{" "}
                {plural(b.pending_count, "change", "changes")} scheduled on it{" "}
                {b.pending_count === 1 ? "is" : "are"} cancelled
              </>
            )}
          </>
        ),
      });
    }
  }

  if (sections.bot_phrases && s.phrases_deleted > 0) {
    operations.push({
      id: "phrases-deleted",
      tone: "danger",
      icon: <Trash size={13} />,
      label: (
        <>
          {plural(s.phrases_deleted, "bot phrase", "bot phrases")} will be
          deleted
        </>
      ),
    });
  }

  // The two bot-source lines — "Bot roster — 201 added, 12 changed, 4
  // removed". A section left unticked is not mentioned at all, and neither is
  // a number that is zero: the list is what this apply will do, not a form.
  const sourceOps: {
    id: string;
    name: string;
    on: boolean;
    added: number;
    changed: number;
    removed: number;
  }[] = [
    {
      id: "bot_roster",
      name: "Bot roster",
      on: rosterPicked,
      added: s.roster_rows_created,
      changed: s.roster_rows_changed,
      removed: s.roster_rows_removed,
    },
    {
      id: "bot_guild_templates",
      name: "Guild templates",
      on: tplPicked,
      added: s.guild_templates_created,
      changed: s.guild_templates_changed,
      removed: s.guild_templates_removed,
    },
  ];
  for (const op of sourceOps) {
    if (!op.on) continue;
    const parts = [
      op.added > 0 ? `${op.added} added` : "",
      op.changed > 0 ? `${op.changed} changed` : "",
      op.removed > 0 ? `${op.removed} removed` : "",
    ].filter(Boolean);
    if (parts.length === 0) continue;
    operations.push({
      id: op.id,
      // A removal is the only half of this that cannot be undone by importing
      // the file again, so it is the half that decides the tint.
      tone: op.removed > 0 ? "danger" : "ok",
      icon: op.removed > 0 ? <Trash size={13} /> : <Plus size={13} />,
      label: (
        <>
          <b>{op.name}</b> — {parts.join(", ")}
        </>
      ),
    });
  }

  // ── The scheduled half of that list (mockup L576-L582) ───────────────────
  //
  // A second list rather than a tone on the first: these rows have not happened
  // when the modal closes, and nobody will be at the screen when they do. Which
  // is exactly why each line ends on the MINUTE it will happen at, taken from
  // the plan: the confirm dialog is the last thing the operator reads before
  // walking away from a change that lands without them.
  //
  // A board the server gave no date for says which cut it waits for instead —
  // see WhenCell for why no date is ever computed here.

  const scheduledOps: ApplyOperation[] = scheduledBoards.map((b) => {
    const taken = b.pending_conflict_fields.length;
    const cut = formatMomentUTC(b.next_cut_at);
    return {
      id: `schedule-${b.ext_key}`,
      tone: "info",
      icon: <Clock size={14} />,
      label: (
        <>
          <b>{b.ext_key}</b> — {boardPayload(b)} ·{" "}
          <b>{cut ?? "at its own next cut"}</b>
        </>
      ),
      sub:
        taken > 0 ? (
          <>
            {taken} of its {b.pending_count} already-scheduled changes{" "}
            {taken === 1 ? "takes" : "take"} the file&apos;s values instead.
          </>
        ) : undefined,
    };
  });

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={styles.root}>
      {fileCard}
      {tiles}

      <AlertStack>
        {reloaded && (
          <Alert
            variant="warn"
            title="The project changed while you were reading"
          >
            Someone else saved this project, so the plan below was reloaded from
            the server. Nothing was applied — read it again before you apply.
          </Alert>
        )}
        {!plan.project_name_matches && (
          <Alert
            variant="warn"
            title="This snapshot came from another project"
            icon={<AlertTriangle size={14} />}
          >
            It was taken from project “{plan.source.project_name}”. Applying it
            to “{projectName}” is allowed — just make sure it is what you meant.
          </Alert>
        )}
        {mirrorAlert}
        {cardKeyAlert}
      </AlertStack>

      {(configCard || boardsCard) && (
        <GroupHead title="Project" apiName="games.config · leaderboards" />
      )}
      {configCard}
      {whenCard}
      {boardsCard}

      {(botSettingsCard || phrasesCard || rosterCard || templatesCard) && (
        <GroupHead
          title="Bots"
          apiName="bot_settings · bot_phrases · bot_roster · bot_guild_templates"
        />
      )}
      {botSettingsCard}
      {phrasesCard}
      {rosterCard}
      {templatesCard}

      <div className={styles.applyBar} role="region" aria-label="Apply snapshot">
        <div className={styles.applyInner}>
          <div className={styles.applyStatus}>
            <span className={styles.applyDot} aria-hidden="true" />
            <span>{plural(sectionsOn, "section", "sections")} selected</span>
            <span className={styles.applyCount}>
              {`· ${joinDots([
                scheduledBoards.length > 0
                  ? `${plural(
                      scheduledBoards.length,
                      "board update",
                      "board updates",
                    )} scheduled for ${
                      scheduledBoards.length === 1 ? "its" : "their"
                    } own next cut`
                  : "",
                scheduledBoards.length > 0
                  ? "everything else applies now"
                  : "everything applies now",
                conflictsSkipped > 0
                  ? `${plural(conflictsSkipped, "conflict", "conflicts")} will be skipped`
                  : "nothing skipped",
              ])}`}
            </span>
          </div>
          <div className={styles.applyActions}>
            <Button variant="ghost" onClick={onCancel} disabled={applying}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => setConfirmOpen(true)}
              disabled={sectionsOn === 0 || applying}
              loading={applying}
              icon={<Check size={14} aria-hidden="true" />}
            >
              Apply snapshot
            </Button>
          </div>
        </div>
      </div>

      <ApplyConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        projectName={projectName}
        operations={operations}
        scheduled={scheduledOps}
        applying={applying}
        onConfirm={() => void apply()}
      />

      <Modal
        open={jsonOpen !== null}
        onClose={() => setJsonOpen(null)}
        title={
          jsonOpen === "bot_settings"
            ? "Bot settings in this snapshot"
            : "Config in this snapshot"
        }
      >
        <p className={styles.jsonLead}>
          The file&apos;s{" "}
          <code>{jsonOpen === "bot_settings" ? "bot_settings" : "config"}</code>{" "}
          object, pretty-printed — this is what the section would install. The
          rows above are the diff against what this environment has today.
        </p>
        <Code variant="block" language="json" className={styles.jsonBlock}>
          {JSON.stringify(
            (jsonOpen === "bot_settings"
              ? snapshot.bot_settings
              : snapshot.config) ?? {},
            null,
            2,
          )}
        </Code>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Two documents, one row shape
// ---------------------------------------------------------------------------

/**
 * The leaf rows of a document diff: a path, a note where the row is a summary,
 * and the two values side by side.
 *
 * Shared by the project config and the bot settings because they ARE the same
 * thing — one JSON document walked leaf by leaf — and a second copy of this
 * markup would be a second place to remember the day a `locked` row learns to
 * render differently.
 */
function DiffRows({ rows }: { rows: ConfigChange[] }) {
  return (
    <>
      {rows.map((c) => (
        <div
          key={c.path}
          className={[styles.diffRow, c.kind === "locked" ? styles.locked : ""]
            .filter(Boolean)
            .join(" ")}
        >
          <div>
            <div className={styles.dPath}>{c.path}</div>
            {c.note && <div className={styles.dNote}>{renderNote(c.note)}</div>}
          </div>
          <div className={styles.dVals}>
            {c.kind === "locked" ? (
              <span className={`${styles.dval} ${styles.kept}`}>{c.new}</span>
            ) : (
              <>
                <span className={`${styles.dval} ${styles.old}`}>{c.old}</span>
                <span className={styles.arrow} aria-hidden="true">
                  →
                </span>
                <span className={`${styles.dval} ${styles.new}`}>{c.new}</span>
              </>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * A heading over a run of section cards — the same two groups the export card
 * lists its rows under, in the same order, because it is the same file.
 *
 * No master checkbox, for the export card's reason: the group is a way to read
 * the list, and every card under it is still a decision of its own.
 */
function GroupHead({ title, apiName }: { title: string; apiName: string }) {
  return (
    <div className={styles.groupHead}>
      <span className={styles.groupTitle}>{title}</span>
      <span className={styles.groupRule} aria-hidden="true" />
      <span className={styles.groupApi}>{apiName}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row rendering — mockup L475-L552
// ---------------------------------------------------------------------------

/** A schema id as the screen says it — see TransferPage's namesake. */
function schemaName(schema: string): string {
  return schema.split("/").pop() ?? schema;
}

function rowClass(b: BoardChange): string | undefined {
  if (b.action === "archive") return styles.removed;
  return undefined;
}

function actionBadge(b: BoardChange): ReactNode {
  switch (b.action) {
    case "update":
      return <Badge variant="info">Update</Badge>;
    case "create":
      return <Badge variant="success">Create</Badge>;
    case "unchanged":
      return <Badge variant="neutral">Unchanged</Badge>;
    default:
      return (
        <Badge variant="warning">{b.hard_delete ? "Delete" : "Archive"}</Badge>
      );
  }
}

/**
 * One of the two moments, as a card — mockup L340-L353.
 *
 * A real radio behind the mockup's drawn circle: the card is a choice of one
 * from two, and the arrow keys, the group's accessible name and the announced
 * "2 of 2" all come free from saying so.
 */
function ApplyModeCard({
  value,
  current,
  title,
  onSelect,
  children,
}: {
  value: ApplyMode;
  current: ApplyMode;
  title: string;
  onSelect: (next: ApplyMode) => void;
  children: ReactNode;
}) {
  const selected = value === current;
  return (
    <label
      className={`${styles.amode} ${selected ? styles.amodeSelected : ""}`}
    >
      <input
        type="radio"
        className={styles.amodeRadio}
        name="apply-mode"
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
      />
      <span>
        <span className={styles.amodeTitle}>{title}</span>
        <span className={styles.amodeDesc}>{children}</span>
      </span>
    </label>
  );
}

/**
 * The line that says a package this board is already holding is about to lose
 * fields to the file — mockup L403.
 *
 * The file's values win, because a file is the later statement about the
 * fields it names; the rest of the package survives untouched. An operator who
 * saw only the count would not know which half is which, and the half that
 * survives is the half they will be surprised by.
 */
function pendingClash(b: BoardChange): ReactNode {
  const taken = b.pending_conflict_fields.length;
  const rest = Math.max(b.pending_count - taken, 0);
  const named = b.pending_conflict_fields.map((f) =>
    // A package keeps its whole prize table in one column rather than under a
    // field name, so `rewards` is written the way the screen says it elsewhere.
    f === "rewards" ? (
      <span key={f}>the prize table</span>
    ) : (
      <code key={f}>{f}</code>
    ),
  );
  return (
    <div className={`${styles.sub} ${styles.subWarn}`}>
      <AlertTriangle size={14} aria-hidden="true" />
      This board already holds{" "}
      {plural(b.pending_count, "scheduled change", "scheduled changes")}.{" "}
      {joinList(named)} {taken === 1 ? "is" : "are"} named by the file too — for{" "}
      {taken === 1 ? "that one" : `those ${word(taken)}`} the file wins
      {rest > 0 ? (
        <>
          ; the other {rest === 1 ? "one stays" : `${word(rest)} stay`} as{" "}
          {rest === 1 ? "it was" : "they were"} scheduled.
        </>
      ) : (
        "."
      )}
    </div>
  );
}

function whatHappens(
  b: BoardChange,
  fromFile: SnapshotLeaderboard | undefined,
): ReactNode {
  switch (b.action) {
    case "update": {
      // A tier_config change moves players between leagues; everything else
      // leaves the cycle and its ranks exactly where they are.
      const tiers = b.fields.some(
        (f) => f === "tier_config" || f.startsWith("tier_config."),
      );
      // A board whose own fields all agree can still be an update: its rewards
      // moved. Saying "0 fields:" and then nothing would read as a bug.
      const rewardsOnly = b.fields.length === 0;
      return (
        <>
          <div>
            {rewardsOnly ? (
              "Board settings already match — only its rewards change"
            ) : (
              <>
                {plural(b.fields.length, "field", "fields")}:{" "}
                {joinNodes(
                  b.fields.map((f) => <code key={f}>{f}</code>),
                  ", ",
                )}
              </>
            )}
          </div>
          {b.pending_conflict_fields.length > 0 ? (
            pendingClash(b)
          ) : (
            <div className={styles.sub}>
              {tiers
                ? "Player leagues are recomputed on their next submit"
                : "Cycle, scores and player ranks are untouched"}
            </div>
          )}
        </>
      );
    }
    case "create": {
      const shape: ReactNode[] = [];
      if (fromFile) {
        shape.push(<code key="w">{fromFile.window_kind}</code>);
        if (fromFile.cron) {
          shape.push(
            <span key="c">
              cron <code>{fromFile.cron}</code>
            </span>,
          );
        }
        shape.push(
          <span key="s">
            segmentation <code>{fromFile.segmentation}</code>
          </span>,
        );
      }
      return (
        <>
          <div>
            Not present here — created from the file together with its first
            cycle
          </div>
          {shape.length > 0 && (
            <div className={styles.sub}>{joinNodes(shape, " · ")}</div>
          )}
        </>
      );
    }
    // Listed only when it is holding a package — see boardRows. Agreeing with
    // the file today and agreeing with it next week are different claims, and
    // this row is the only place the screen can tell them apart.
    case "unchanged":
      return (
        <>
          <div>The file and this environment already agree</div>
          <div className={`${styles.sub} ${styles.subWarn}`}>
            <AlertTriangle size={14} aria-hidden="true" />
            {plural(b.pending_count, "change is", "changes are")} scheduled
            here. After {b.pending_count === 1 ? "it applies" : "they apply"},
            this board will differ from the file again.
          </div>
        </>
      );
    default:
      return (
        <>
          <div>Present here, absent from the file</div>
          <div className={styles.sub}>
            {b.hard_delete
              ? "It holds no scores, so there is no player history to preserve — it is deleted outright"
              : `It holds ${count(b.scores)} scores, so it is archived rather than deleted — player history stays`}
            {b.pending_count > 0 &&
              ` — and archiving also cancels the ${plural(
                b.pending_count,
                "change",
                "changes",
              )} scheduled on it`}
          </div>
        </>
      );
  }
}

/**
 * The When column — mockup L389 and the cells at L405-L489.
 *
 * The cut's date comes from the plan (`next_cut_at`, read off the board's own
 * open or scheduled cycle) and is never computed here: a board's next boundary
 * follows its cron, and a date derived in the browser would be wrong for
 * exactly the boards whose cron this very file is about to change. A row the
 * server gave no date keeps the sentence and drops the minute — which is what
 * a board whose worker owes it an overdue cut looks like.
 */
function WhenCell({
  board,
  mode,
  onPick,
}: {
  board: BoardChange;
  mode: ApplyMode;
  onPick: (next: ApplyMode) => void;
}) {
  if (board.action === "unchanged") {
    return (
      <>
        —<div className={styles.sub}>nothing to apply</div>
      </>
    );
  }

  if (board.action === "create" || board.action === "archive") {
    return (
      <>
        <b className={styles.whenMoment}>Now</b>
        <div className={styles.sub}>
          {board.action === "create"
            ? "a board that does not exist here has no cycle to wait for"
            : "an archived board has no next cycle"}
        </div>
      </>
    );
  }

  const stranded = board.schedule_only_fields;

  // An update on a board with no cut to wait for. It lands now whatever the
  // card above says — and if it names a field only a cut can write, that field
  // lands nowhere at all, which is the one thing an operator must not have to
  // discover afterwards.
  if (!board.schedulable) {
    return (
      <>
        <b className={styles.whenMoment}>Now</b>
        <div className={styles.sub}>
          this board has no next cycle to wait for
        </div>
        {stranded.length > 0 && (
          <div className={`${styles.sub} ${styles.subWarn}`}>
            <AlertTriangle size={14} aria-hidden="true" />
            {joinList(stranded.map((f) => <code key={f}>{f}</code>))} can only
            be written at a cut, so {stranded.length === 1 ? "it" : "they"} will
            not be applied.
          </div>
        )}
      </>
    );
  }

  // A row naming a schedule-only field has no `Now` to offer: those leaves have
  // no immediate write path at all, here or on the board's own Settings tab.
  const forced = stranded.length > 0;
  const why = forced
    ? `${stranded.join(", ")} can only be written when a cycle closes`
    : undefined;
  const cut = formatMomentUTC(board.next_cut_at);

  return (
    <>
      <b className={styles.whenMoment}>
        {mode === "next_cycle" ? "Next cycle" : "Now"}
      </b>
      <div className={styles.sub}>
        {mode === "next_cycle"
          ? cut
            ? `${cut} — when this board's current cycle closes`
            : "when this board's current cycle closes"
          : "the moment you confirm"}
      </div>
      <div
        className={styles.whenToggle}
        role="group"
        aria-label={`When ${board.ext_key} applies`}
      >
        <button
          type="button"
          className={`${styles.whenOpt} ${mode === "now" ? styles.whenOptOn : ""}`}
          /* aria-disabled rather than `disabled`, the same choice the board's
             own save bar makes: the half that cannot be picked is the half
             with something to say, and a disabled button is not focusable, so
             nobody reading the page with a keyboard would ever reach the
             reason. It also renders no native tooltip in Chromium, which is
             why the title used to be duplicated onto the wrapper. The click
             is refused here instead. */
          aria-disabled={forced || undefined}
          title={why}
          aria-pressed={mode === "now"}
          onClick={() => {
            if (forced) return;
            onPick("now");
          }}
        >
          Now
        </button>
        <button
          type="button"
          className={`${styles.whenOpt} ${
            mode === "next_cycle" ? styles.whenOptOn : ""
          }`}
          aria-pressed={mode === "next_cycle"}
          onClick={() => onPick("next_cycle")}
        >
          Next cycle
        </button>
      </div>
    </>
  );
}

/**
 * What a scheduled row carries, in the modal's shorthand (mockup L578-L581).
 *
 * A single top-level field is named, because that IS the fact — a board
 * changing its `window_kind` — while `tier_config.tiers[2].min_score` is a
 * path nobody reads in a confirm dialog, and is counted instead.
 */
function boardPayload(b: BoardChange): ReactNode {
  const only = b.fields.length === 1 ? b.fields[0] : undefined;
  const parts: ReactNode[] = [];
  if (only !== undefined && !only.includes(".")) {
    parts.push(<code key="f">{only}</code>);
  } else if (b.fields.length > 0) {
    parts.push(<span key="f">{plural(b.fields.length, "field", "fields")}</span>);
  }
  if (b.rewards.created + b.rewards.updated + b.rewards.deleted > 0) {
    parts.push(<span key="r">the prize table</span>);
  }
  return <>{joinList(parts)}</>;
}

/** `+2 · 4 same` — the reward tally for one board (mockup L486, L499). */
function rewardsCell(b: BoardChange): ReactNode {
  if (b.action === "archive") {
    return <span className={styles.deltaNone}>—</span>;
  }
  const r = b.rewards;
  const parts: ReactNode[] = [];
  if (r.created > 0) {
    parts.push(
      <span key="c" className={styles.deltaPlus}>
        +{r.created}
      </span>,
    );
  }
  if (r.updated > 0) {
    parts.push(
      <span key="u" className={styles.deltaChanged}>
        ~{r.updated}
      </span>,
    );
  }
  if (r.deleted > 0) {
    parts.push(
      <span key="d" className={styles.deltaMinus}>
        −{r.deleted}
      </span>,
    );
  }
  if (r.unchanged > 0) {
    parts.push(
      <span key="s" className={styles.deltaNone}>
        {parts.length > 0 ? "· " : ""}
        {r.unchanged} same
      </span>,
    );
  }
  if (parts.length === 0) return <span className={styles.deltaNone}>—</span>;
  return <>{joinNodes(parts, " ")}</>;
}

// ---------------------------------------------------------------------------
// The bot source — mockup L427-L479
// ---------------------------------------------------------------------------

/**
 * What each roster bucket is called and what it means, in the vocabulary
 * internal/admin/snapshot/rosterdiff.go defines rather than a paraphrase of it.
 *
 * Two of the six carry the argument this whole section makes and their copy has
 * to say it. A row whose name a living player here already carries is NOT a
 * conflict — it lands, blocked, and is fixed exactly like a collision that
 * happened here — while a row naming a card key this project never declared IS
 * one, and is applied by nothing. `kept` is the third that needs care: the
 * backend puts two different rows in it, a row already spent on a bot and a row
 * already identical to the file, and both have to be true of the sentence or
 * the footer's sum stops accounting for every row.
 */
const ROSTER_BUCKETS: Record<
  RosterAction,
  { label: string; tone: "ok" | "info" | "warn" | "bad" | "neutral"; text: string }
> = {
  created: {
    label: "Created",
    tone: "ok",
    text: "rows this project does not have",
  },
  changed: {
    label: "Changed",
    tone: "info",
    text: "rows both sides have, whose card, place in the order or blocked state the file moves",
  },
  removed: {
    label: "Removed",
    tone: "warn",
    text: "unspent rows this project has and the file does not. A row a bot is standing on is never removed",
  },
  kept: {
    label: "Kept",
    tone: "neutral",
    text: "the file changes nothing here — already spent on a bot, so beyond an import's reach, or already identical to the file",
  },
  blocked: {
    label: "Will arrive blocked",
    tone: "bad",
    text: "the name belongs to a living player in this project. The row still lands, and is fixed on the Source tab exactly like a local collision — so it is counted again above, in whichever of the five it belongs to, and never in the sum",
  },
  conflict: {
    label: "Conflict — not applied",
    tone: "bad",
    text: "the card names a key this project's schema does not declare. Declare the key first, then import again",
  },
};

const BUCKET_TONE_CLASS: Record<
  (typeof ROSTER_BUCKETS)[RosterAction]["tone"],
  string
> = {
  neutral: "",
  ok: styles.countOk,
  info: styles.countInfo,
  warn: styles.countWarn,
  bad: styles.countBad,
};

/**
 * One bucket: a count, a sentence, and a couple of sample rows behind a
 * disclosure. The samples are whatever the plan carried — at most two — and the
 * "…and N more" under them is `count - samples.length`, so the two numbers on
 * screen come from one place.
 */
function RosterBucket({
  group,
  open,
  onToggle,
}: {
  group: RosterGroupPlan;
  open: boolean;
  onToggle: () => void;
}) {
  const bucket = ROSTER_BUCKETS[group.action];
  const more = group.count - group.samples.length;
  return (
    <div className={styles.bucket}>
      <button
        type="button"
        className={styles.bucketRow}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span
          className={[styles.bucketCount, BUCKET_TONE_CLASS[bucket.tone]]
            .filter(Boolean)
            .join(" ")}
        >
          {group.count}
        </span>
        <span className={styles.bucketText}>
          <b>{bucket.label}</b> — {bucket.text}
        </span>
        <span className={styles.bucketChevron} aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div className={styles.bucketSamples}>
          {group.samples.map((row) => (
            <div className={styles.sampleLine} key={row.name}>
              <span className={styles.sampleName}>{row.name}</span>
              {/* The overlay, drawn where the row actually shows. A blocked row
                  is counted in created, changed or kept as well as in its own
                  bucket, so a sample under «Created» has to be able to say it
                  lands blocked. Inside the blocked bucket the heading already
                  says it, and a badge there would only repeat itself. */}
              {row.blocked && group.action !== "blocked" && (
                <Badge variant="danger">arrives blocked</Badge>
              )}
              <span>{rosterSampleDetail(row)}</span>
            </div>
          ))}
          {more > 0 && (
            <div className={`${styles.sampleLine} ${styles.sampleMore}`}>
              …and {more} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * What is printed beside a sample's name. The reason wins where the plan sent
 * one — for a blocked or conflicting row it is the whole point of the line —
 * and everywhere else it is the card that will actually be written.
 */
function rosterSampleDetail(row: RosterRowPlan): string {
  return (
    row.reason ??
    cardSummary(row.avatar, row.country, row.profile, row.stats, row.meta)
  );
}

/** `warrior_1 · US · level 12 · trophies 340` — a card as one line. */
function cardSummary(
  avatar: string | undefined,
  country: string | undefined,
  profile: string | undefined,
  ...cards: (Record<string, unknown> | undefined)[]
): string {
  const parts: string[] = [];
  // First, as it is in every other place a row is read. Without it a row that
  // travels with nothing but a new avatar sits in Changed beside a sample
  // identical to the one above it.
  if (avatar) parts.push(avatar);
  if (country) parts.push(country.toUpperCase());
  if (profile) parts.push(profile);
  for (const card of cards) {
    for (const [key, value] of Object.entries(card ?? {})) {
      parts.push(`${key} ${cardValue(value)}`);
    }
  }
  return parts.join(" · ");
}

/** One card value as an operator wrote it, not as JSON quotes it. */
function cardValue(v: unknown): string {
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(cardValue).join(", ");
  return JSON.stringify(v) ?? String(v);
}

/**
 * One side of a guild template, or the "—" of a side that has nothing.
 *
 * EVERY half of it, never one: a template carries a description, the logo its
 * guild will wear and whatever `guild.metadata_schema` declares, and the diff
 * behind this table treats a change to any of them as a change. Printing the
 * metadata and falling back to the description only when there was none meant
 * that a template with any metadata at all never showed its description — so a
 * description-only edit painted a row badged «changed» whose two columns were
 * character for character the same, and a logo left out here would do it
 * again.
 */
function templateSide(t: SnapshotGuildTemplate | null): ReactNode {
  if (!t) return <span className={styles.deltaNone}>—</span>;
  const summary = cardSummary(undefined, undefined, undefined, t.metadata);
  if (!t.description && !t.logo && !summary) {
    return (
      <span className={styles.deltaNone}>
        no description, no logo, no metadata
      </span>
    );
  }
  return (
    <>
      {t.description && <div>{t.description}</div>}
      {t.logo && <div className={styles.monoCell}>logo {t.logo}</div>}
      {summary && <div className={styles.monoCell}>{summary}</div>}
    </>
  );
}

/** The same halves as one string, for asking whether the two columns of a row
 *  say anything different at all. */
function templateSideText(t: SnapshotGuildTemplate | null): string {
  if (!t) return "";
  return [
    t.description ?? "",
    t.logo ? `logo ${t.logo}` : "",
    cardSummary(undefined, undefined, undefined, t.metadata),
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Why a row is badged «changed» when its two columns read alike.
 *
 * The diff also compares the row's POSITION and the block it carries, and the
 * file sends neither as a column — so reordering a file badges every row
 * changed with, without this line, nothing on screen to explain it. It says
 * both possibilities rather than guessing between them: the plan does not
 * carry the position that would let the browser tell which.
 */
function templateInvisibleChange(t: GuildTemplatePlan): string | null {
  if (t.action !== "changed" || t.reason) return null;
  if (templateSideText(t.here) !== templateSideText(t.in_file)) return null;
  return "same on both sides — what the file moves is the row's position in the table, or the block it carries";
}

const TEMPLATE_BADGES: Record<TemplateAction, ReactNode> = {
  created: <Badge variant="success">created</Badge>,
  changed: <Badge variant="info">changed</Badge>,
  removed: <Badge variant="warning">removed</Badge>,
  kept: <Badge variant="neutral">kept</Badge>,
  conflict: <Badge variant="danger">conflict — not applied</Badge>,
};

/**
 * The Result cell: what the import will DO to this template, and — beside it,
 * never instead of it — whether the template lands blocked.
 *
 * Two badges because they answer two questions. The template is written, and it
 * is written as created, changed or kept; that a guild here already carries its
 * name is a state of the row afterwards, fixed on the Guild templates tab
 * exactly like a collision that happened here. A single badge saying only
 * «blocked» hid which of the three the row actually was, and made the counts
 * above it look wrong.
 */
function templateBadge(t: GuildTemplatePlan): ReactNode {
  if (!t.blocked) return TEMPLATE_BADGES[t.action];
  return (
    <>
      {TEMPLATE_BADGES[t.action]}{" "}
      <Badge variant="danger">arrives blocked — guild name taken</Badge>
    </>
  );
}

/**
 * Only the two rows whose fate the tint is about: a template this environment
 * loses, and one nothing will write.
 *
 * `blocked` is deliberately not among them, and now cannot be: it is a flag
 * beside the action rather than one of its values, and the actions it can sit
 * on — created, changed, kept — are exactly the untinted three. A blocked
 * template lands like any other, so tinting it would say the opposite of the
 * badge next to it.
 */
function templateRowClass(t: GuildTemplatePlan): string | undefined {
  if (t.action === "conflict") return styles.skipped;
  if (t.action === "removed") return styles.removed;
  return undefined;
}

/** The phrase table's Change cell (mockup L589-L594). */
function phraseChangeCell(g: PhraseGroupChange): ReactNode {
  const parts: ReactNode[] = [];
  if (g.added > 0) {
    parts.push(
      <span key="a" className={styles.deltaPlus}>
        +{g.added}
      </span>,
    );
  }
  if (g.deleted > 0) {
    parts.push(
      <span key="d" className={styles.deltaMinus}>
        −{g.deleted}
      </span>,
    );
  }
  if (g.status_changed > 0) {
    parts.push(
      <span key="s" className={styles.deltaChanged}>
        ~{g.status_changed}
      </span>,
    );
  }
  if (parts.length === 0) {
    return <span className={styles.deltaNone}>unchanged</span>;
  }
  return <>{joinNodes(parts, " ")}</>;
}
